import {
  getStateFromPath,
  NavigatorScreenParams,
  ParamListBase,
  RouteProp,
} from '@react-navigation/native'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'

import { CategoryIdEnum, CulturalSurveyQuestionEnum } from 'api/gen/api'
import { DisabilitiesProperties } from 'features/accessibility/types'
import { Provider } from 'features/auth/types'
import { BookingsTab } from 'features/bookings/enum'
import { Color, OffersModuleParameters, VenuesModule } from 'features/home/types'
import { CheatcodesStackParamList } from 'features/navigation/navigators/CheatcodesStackNavigator/types'
import { OnboardingStackParamList } from 'features/navigation/navigators/OnboardingStackNavigator/types'
import { ProfileStackParamList } from 'features/navigation/navigators/ProfileStackNavigator/types'
import { SearchStackParamList } from 'features/navigation/navigators/SearchStackNavigator/types'
import { SubscriptionStackParamList } from 'features/navigation/navigators/SubscriptionStackNavigator/types'
import { TabParamList } from 'features/navigation/navigators/TabNavigator/types'
import { PlaylistType } from 'features/offer/enums'
import { SearchState } from 'features/search/types'
import { Venue } from 'features/venue/types'
import {
  ConsultArtistOriginDetails,
  FakeDoorAnalyticsParams,
} from 'libs/analytics/logEventAnalytics'
import { ContentfulLabelCategories } from 'libs/contentful/types'
import { SuggestedPlace } from 'libs/place/types'
import { StorageKey } from 'libs/storage'
import { VerticalPlaylistOffersSource } from 'shared/verticalPlaylist/types'

export type Referrals =
  | Lowercase<keyof AllNavParamList>
  | 'artist'
  | 'bookingimpossible'
  | 'chronicles'
  | 'deeplink'
  | 'endedbookings'
  | 'exclusivity'
  | 'highlightOffer'
  | 'home'
  | 'search'
  | 'searchAutoComplete'
  | 'searchLanding'
  | 'searchPlaylist'
  | 'searchVenuePlaylist'
  | 'setemail'
  | 'similar_offer'
  | 'trend_block'
  | 'venue'
  | 'venueMap'
  | 'venueList'
  | 'video'
  | 'videoModal'
  | 'video_carousel_block'
  | 'comingSoonOffer'

type BaseThematicHome = {
  homeId: string
  videoModuleId?: string
  moduleId?: string
  moduleListId?: string
  moduleItemId?: string
  latitude?: number
  longitude?: number
}

type OtherThematicBlockHome = {
  from?: 'deeplink' | 'chronicles' | 'venue'
}

type CategoryBlockThematicHome = {
  from: 'category_block'
}
type HighlightThematicBlockThematicHome = {
  from: 'highlight_thematic_block'
}

export type ThematicHomeParams = BaseThematicHome &
  (OtherThematicBlockHome | CategoryBlockThematicHome | HighlightThematicBlockThematicHome)

export type AccessibilityRootStackParamList = {
  Accessibility?: Record<string, unknown> // I had to put type Record<string, unknown> so that getProfileHookConfig in DeeplinksGeneratorForm can take appAndMarketingParams, otherwise I would have just put undefined.
  AccessibilityDeclarationMobileAndroid?: undefined
  AccessibilityDeclarationMobileIOS?: undefined
  AccessibilityDeclarationWeb?: undefined
  SiteMapScreen?: undefined
  PublicDisabilityServices?: undefined
}

export type CulturalSurveyRootStackParamList = {
  CulturalSurveyIntro: undefined
  CulturalSurveyQuestions?: { question: CulturalSurveyQuestionEnum | undefined }
  CulturalSurveyThanks: undefined
  FAQWebview: undefined
}

type TrustedDeviceRootStackParamList = {
  AccountSecurity: {
    token: string
    email: string
    reset_password_token: string
    reset_token_expiration_timestamp: number
  }
  AccountSecurityBuffer: {
    token: string
    email: string
    reset_password_token: string
    reset_token_expiration_timestamp: number
  }
  SuspensionChoice: { token: string }
  SuspensionChoiceExpiredLink: undefined
  SuspiciousLoginSuspendedAccount: undefined
}

export enum StepperOrigin {
  BOOKING = 'booking',
  DEACTIVATE_PROFILE_SUCCESS = 'DeactivateProfileSuccess',
  DEEPLINK = 'deeplink',
  FAVORITE = 'favorite',
  FORGOTTEN_PASSWORD = 'forgottenPassword',
  HOME = 'home',
  LOGIN = 'login',
  LOGIN_METHODS = 'LoginMethods',
  LOGIN_METHODS_WITH_LAST_LOGIN_INFO = 'LoginMethodsWithLastLoginInfo',
  NOTIFICATION = 'notification',
  OFFER = 'offer',
  ONBOARDING_GENERAL_PUBLIC_WELCOME = 'OnboardingGeneralPublicWelcome',
  ONBOARDING_NOT_ELIGIBLE = 'onboardingNotEligible',
  ONBOARDING_WELCOME = 'onboardingWelcome',
  PROFILE = 'profile',
  RESET_PASSWORD_EMAIL_SENT = 'resetPasswordEmailSent',
  SIGNUP = 'signup',
  SIGNUP_METHODS = 'SignupMethods',
  THEMATIC_HOME = 'thematicHome',
  TUTORIAL = 'Tutorial',
  VALIDATE_EMAIL_CHANGE = 'validateEmailChange',
  VERIFY_ELIGIBILITY = 'verifyEligibility',
}

type OfferParams = {
  id: number
  from?: Referrals
  moduleName?: string
  moduleId?: string
  fromOfferId?: number
  fromMultivenueOfferId?: number
  openModalOnNavigation?: boolean
  searchId?: string
  apiRecoParams?: string
  playlistType?: PlaylistType
}

type OfferPreviewParams = {
  id: number
  defaultIndex?: number
}

type OfferVideoPreviewParams = {
  id: number
}

type BookingDetailsParams = {
  id: number
}

type BookingConfirmationParams = {
  offerId: number
  bookingId: number
  apiRecoParams?: string
}

type AfterSignupEmailValidationBufferParams = {
  token: string
  expiration_timestamp: number
  email: string
}

type SignupParams =
  | {
      accountCreationToken?: string
      email?: string
      offerId?: number
      from: StepperOrigin
      ssoProvider?: Provider.GOOGLE | Provider.APPLE
      stepIndex?: number
    }
  | undefined

type VenueParams = {
  id: number
  from?: Referrals
  searchId?: string
  fromThematicSearch?: ContentfulLabelCategories
}

type VenuePreviewCarouselParams = {
  id: number
  defaultIndex?: number
}

type ArtistParams = {
  id: string
}

type ClubAdvicesParams = {
  offerId: number
  adviceId?: number
  from?: Referrals
}

type ProAdvicesOfferParams = {
  offerId: number
  venueId?: number
}

type ProAdvicesVenueParams = {
  venueId: number
  offerId?: number
}

type VideoModulePageParams = {
  moduleId: string
  moduleName: string
  homeEntryId: string
  offersModuleParameters: OffersModuleParameters[]
  youtubeVideoId: string
  isMultiOffer: boolean
  videoTag: string
  videoPublicationDate: string
  videoDescription: string
  offerTitle: string
  color: Color
  videoTitle: string
  offerIds?: string[]
  eanList?: string[]
  transcription: string
  thematicHomeEntryId?: string
  thematicHomeTitle?: string
}

type LoginParams = {
  displayForcedLoginHelpMessage?: boolean
  offerId?: number
  from?: StepperOrigin
}

type FakeDoorModalParams = {
  surveyKey: StorageKey
  surveyUrl: string
  analyticsParams?: FakeDoorAnalyticsParams
}

/**
 * WARNING !
 * Deeplink: When updating the screen parameters, pay attention to the deeplink handlers.
 * If a deeplink handler indexes the screen with params you are changing,
 * please update the deeplink handler in consequence.
 */
export type RootStackParamList = {
  ABTestingPOC: undefined
  AccountCreated: undefined
  AccountReactivationSuccess: undefined
  AccountStatusScreenHandler: undefined
  Achievements: { from: 'profile' | 'success' } | undefined
  AfterSignupEmailValidationBuffer: AfterSignupEmailValidationBufferParams
  _DeeplinkOnlyAfterSignupEmailValidationBuffer1: AfterSignupEmailValidationBufferParams
  Artist: ArtistParams
  ArtistWebview: ArtistParams
  _DeeplinkOnlyArtist1: ArtistParams
  BannedCountryError: undefined
  IncorrectLink: undefined
  BonificationGranted: undefined
  BookingConfirmation: BookingConfirmationParams
  _DeeplinkOnlyBookingConfirmation1: BookingConfirmationParams
  BookingDetails: BookingDetailsParams
  _DeeplinkOnlyBookingDetails1: BookingDetailsParams
  Bookings: { activeTab?: BookingsTab } | undefined
  ChangeEmailExpiredLink: undefined
  CheatcodesStackNavigator?: NavigatorScreenParams<CheatcodesStackParamList>
  ClubAdvices: ClubAdvicesParams
  _DeeplinkOnlyClubAdvices1: ClubAdvicesParams
  CulturalSurvey: undefined
  DeeplinksGenerator: undefined
  EighteenBirthday: undefined
  _DeeplinkOnlyEighteenBirthday1: undefined
  FakeDoorModal: FakeDoorModalParams
  FavoritesSorts: undefined
  ForgottenPassword: undefined
  FraudulentSuspendedAccount: undefined
  LocationFilter?: { selectedVenue?: Venue; selectedPlace?: SuggestedPlace }
  LocationPicker: undefined
  Login?: LoginParams
  LoginMethods?: LoginParams
  LoginMethodsWithLastLoginInfo?: LoginParams
  Maintenance: undefined
  MandatoryUpdatePersonalData: undefined
  MovieCalendar: undefined
  NotYetUnderageEligibility: { eligibilityStartDatetime: string }
  Offer: OfferParams
  _DeeplinkOnlyOffer1: OfferParams
  _DeeplinkOnlyOffer2: OfferParams
  _DeeplinkOnlyOffer3: OfferParams
  OfferDescription: { id: number }
  OfferPreview: OfferPreviewParams
  _DeeplinkOnlyOfferPreview1: OfferPreviewParams
  _DeeplinkOnlyOfferPreview2: OfferPreviewParams
  _DeeplinkOnlyOfferPreview3: OfferPreviewParams
  OfferVideoPreview: OfferVideoPreviewParams
  OnboardingStackNavigator?: NavigatorScreenParams<OnboardingStackParamList>
  AppleSSOCallback: { code?: string; state?: string; error?: string } | undefined
  OnboardingSubscription: undefined
  PageNotFound: undefined
  ProAdvicesOffer: ProAdvicesOfferParams
  ProAdvicesVenue: ProAdvicesVenueParams
  Profile: undefined
  ProfileStackNavigator?: NavigatorScreenParams<ProfileStackParamList>
  RecreditBirthdayNotification: undefined
  _DeeplinkOnlyRecreditBirthdayNotification1: undefined
  ReinitializePassword: {
    email: string
    token: string
    expiration_timestamp: number
    from?: Referrals
  }
  ResetPasswordEmailSent: { email: string }
  ResetPasswordExpiredLink: { email: string }
  SearchFilter?: Partial<SearchState & { accessibilityFilter: Partial<DisabilitiesProperties> }>
  SignupConfirmationEmailSent: { email: string }
  SignupConfirmationExpiredLink: { email: string }
  SignupMethods: SignupParams
  _DeeplinkOnlySignupMethods1: SignupParams
  SignupForm: SignupParams
  _DeeplinkOnlySignupForm1: SignupParams
  SubscriptionStackNavigator?: NavigatorScreenParams<SubscriptionStackParamList>
  TabNavigator: NavigatorScreenParams<TabParamList>
  ThematicHome: ThematicHomeParams
  _DeeplinkOnlyThematicHome1: ThematicHomeParams
  Tutorial?: { selectedAge?: 15 | 16 | 17 | 18 }
  UTMParameters: { utm_campaign: string; utm_medium: string; utm_source: string } | undefined
  ValidateEmailChange: { token: string }
  Venue: VenueParams
  _DeeplinkOnlyVenue1: VenueParams
  VenueMap: undefined
  VenueMapFiltersStackNavigator: undefined
  VenuePreviewCarousel: VenuePreviewCarouselParams
  _DeeplinkOnlyVenuePreviewCarousel1: VenuePreviewCarouselParams
  _DeeplinkOnlyVenuePreviewCarousel2: VenuePreviewCarouselParams
  _DeeplinkOnlyVenuePreviewCarousel3: VenuePreviewCarouselParams
  VerifyEligibility: undefined
  VerticalPlaylistArtists: {
    title: string
    subtitle?: string
    venueId?: number
    similarToArtistId?: string
    offerId?: number
    offerCategoryId?: CategoryIdEnum
    originDetails?: ConsultArtistOriginDetails
  }
  VerticalPlaylistOffers: VerticalPlaylistOffersSource
  VerticalPlaylistVenues: { module: VenuesModule }
  VideoModulePage: VideoModulePageParams
  HomeLocationModal: undefined
  SearchLocationModal: undefined
  VenueMapLocationModal: {
    openedFrom: Referrals
  }
  GeolocationActivationModal: undefined
} & TrustedDeviceRootStackParamList

export type AllNavParamList = RootStackParamList &
  TabParamList &
  SearchStackParamList &
  ProfileStackParamList &
  OnboardingStackParamList &
  SubscriptionStackParamList &
  CheatcodesStackParamList

/** Type helpers to share screen names */
export type RootScreenNames = keyof RootStackParamList
export type ScreenNames = keyof AllNavParamList

/**
 * Type helper for useRoute
 *
 * const {
 *  params: { token, expiration_timestamp },
 * } = useRoute<UseRouteType<'ReinitializePassword'>>()
 */
export type UseRouteType<ScreenName extends ScreenNames> = RouteProp<AllNavParamList, ScreenName>
/**
 * Type helper for useNavigation in the context of a Stack Navigator
 * Cf. https://reactnavigation.org/docs/navigation-prop/#navigator-dependent-functions
 *
 * const navigation = useNavigation<UseNavigationType>()
 */
export type UseNavigationType = NativeStackNavigationProp<RootStackParamList>
/**
 * Type helper to access route params
 *
 * export type MyStackParamList = {
 *   Login?: { userId: string }
 * }
 *
 * RouteParams<'Login', MyStackParamList>  // will return ({ userId: string } | undefined)
 */
export type RouteParams<
  StackParamList extends Record<string, unknown>,
  ScreenName extends keyof StackParamList,
> = Pick<StackParamList, ScreenName>[ScreenName]

type NavigateParams<RouteName extends keyof ParamListBase> =
  undefined extends ParamListBase[RouteName]
    ? [RouteName] | [RouteName, ParamListBase[RouteName]]
    : [RouteName] | [RouteName, ParamListBase[RouteName]]
export type RootNavigateParams = NavigateParams<keyof RootStackParamList>
export type ProfileNavigateParams = NavigateParams<keyof ProfileStackParamList>
export type AllNavigateParams = NavigateParams<keyof AllNavParamList>

export type NavigationResultState = ReturnType<typeof getStateFromPath>

// Typeguard for screen params
export function isScreen<Screen extends AllNavigateParams[0]>(
  expectedScreen: Screen,
  screen: AllNavigateParams[0],
  _params: AllNavigateParams[1]
): _params is AllNavParamList[Screen] {
  return screen === expectedScreen
}
